<template>
    <Modal class="cart-line-details-modal"
           size="lg">
        <template v-slot:header>
            <div class="modl-header">
                <ModalTabs :tabs="visibleTabs"
                           :selected-tab="selectedTab"
                           @select-tab="selectTab"></ModalTabs>
            </div>
        </template>

        <template v-slot:body>
            <component :is="selectedTab.component"
                       v-if="selectedTab"
                       ref="current-tab-component"
                       :cart-line="cartLine"
                       :cancel-label="cancelLabel"
                       @cancel="cancel"
                       @resolve="resolve" />
        </template>
    </Modal>
</template>

<script>
import CartLineFieldsTab from '@/epos/vue/modals/CartLineDetails/CartLineFieldsTab';
import CartLineOffersTab from '@/epos/vue/modals/CartLineDetails/CartLineOffersTab';
import CartLineReturnsTab from '@/epos/vue/modals/CartLineDetails/CartLineReturnsTab';
import ItemType from '@/lib/Items/ItemType';
import Modal from '@/vue/components/Modals/Modal';
import ModalTabs from '@/vue/components/Modals/ModalTabs';
import { CartLineDetailsTabNameEnum } from '@/epos/vue/modals/CartLineDetails/CartLineDetailsTabNameEnum';
import { CloseModalOnEscapeMixin } from '@/vue/mixins/CloseModalOnEscapeMixin';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { TabbedModalMixin } from '@/vue/mixins/TabbedModalMixin';

export default {
    components: {
        Modal,
        ModalTabs,
        CartLineFieldsTab,
        CartLineOffersTab,
        CartLineReturnsTab,
    },

    mixins: [
        ModalPropsMixin,
        TabbedModalMixin,
        CloseModalOnEscapeMixin,
    ],

    props: {
        cancelLabel: {
            type: String,
            default: 'CANCEL',
        },

        cartLine: {
            type: Object,
            required: true,
        },

        initialTabName: {
            type: String,
            default: null,
        },

        triggeredByItemButton: {
            type: Boolean,
            required: false,
        },

        // Only show the offers tab?
        onlyOffers: {
            type: Boolean,
            default: false,
        },

        // Only show the options/fields tab?
        onlyFields: {
            type: Boolean,
            default: false,
        },

        // Only show the returns tab?
        onlyReturns: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        showFieldsTab() {
            return !this.onlyOffers
                && !this.onlyReturns;
        },

        showOffersTab() {
            return !this.onlyFields
                && !this.onlyReturns;
        },

        showReturnsTab() {
            return !this.onlyOffers
                && !this.onlyFields;
        },

        /**
         * @returns {{name: string, icon: string, label: string, component: string}[]}
         */
        visibleTabs() {
            return this.tabs.filter((tab) => {
                switch (tab.name) {
                    case CartLineDetailsTabNameEnum.FIELDS:
                        return this.showFieldsTab && this.fieldsApplicable;
                    case CartLineDetailsTabNameEnum.OFFERS:
                        return this.showOffersTab && this.offersApplicable;
                    case CartLineDetailsTabNameEnum.RETURNS:
                        return this.showReturnsTab && this.returnsApplicable;
                }

                return true;
            });
        },

        /**
         * Is the ask early fields tab applicable to the current cart line?
         *
         * @returns {boolean}
         */
        fieldsApplicable() {
            if (this.initialTabName === CartLineDetailsTabNameEnum.FIELDS) {
                // If the fields tab was requested force it to show.
                return true;
            }

            // Only show the fields tab for non-refunds.
            // TODO: Showing fields for refund lines is useful if there are chargeable product options.
            // but see the comment on https://digitickets.atlassian.net/browse/PROP-1450 for why it is disabled.
            return this.cartLine.getQuantity() > 0;
        },

        /**
         * Is the offers tab applicable to the current cart line?
         *
         * @returns {boolean}
         */
        offersApplicable() {
            // This doesn't do much, but it is here for completeness.
            return true;
        },

        /**
         * Is the returns tab applicable to the current cart line?
         *
         * @returns {boolean}
         */
        returnsApplicable() {
            // If the returns tab was requested force it to show.
            // This is used because the return reasons tab is shown when initially changing a line
            // from a positive to a negative quantity. At that point the qty of the line is still positive.
            if (this.initialTabName === CartLineDetailsTabNameEnum.RETURNS) {
                return true;
            }

            if (
                [ItemType.TICKET, ItemType.PRODUCT].includes(this.cartLine.getItemType())
                && this.cartLine.getQuantityDifference() < 0
            ) {
                return true;
            }

            return false;
        },
    },

    created() {
        this.tabs.push({
            name: CartLineDetailsTabNameEnum.FIELDS,
            icon: 'fa-ballot-check',
            label: 'ITEM_EDIT.ITEM_OPTIONS',
            component: 'CartLineFieldsTab',
        });

        this.tabs.push({
            name: CartLineDetailsTabNameEnum.OFFERS,
            icon: 'fa-badge-percent',
            label: 'ITEM_EDIT.DISCOUNTS',
            component: 'CartLineOffersTab',
        });

        this.tabs.push({
            name: CartLineDetailsTabNameEnum.RETURNS,
            icon: 'fa-turn-down-left',
            label: 'ITEM_EDIT.RETURN_REASONS',
            component: 'CartLineReturnsTab',
        });

        this.selectInitialTab();
    },

    methods: {
        selectInitialTab() {
            // If a specific tab was requested. Check if it exists and select it if so.
            if (this.initialTabName) {
                const initialTab = this.visibleTabs.find((t) => t.name === this.initialTabName);
                if (initialTab) {
                    this.selectTab(initialTab);
                    return;
                }
            }

            // No specific tab was requested. Use a sensible default.

            // If the quantity is negative, show the returns tab by default, if the tab is available.
            if (this.cartLine.getQuantity() < 0) {
                const returnsTab = this.visibleTabs.find((t) => t.name === CartLineDetailsTabNameEnum.RETURNS);
                if (returnsTab) {
                    this.selectTab(returnsTab);
                    return;
                }
            }

            // Otherwise, show the first tab. (Probably fields).
            if (this.visibleTabs.length > 0) {
                this.selectTab(this.visibleTabs[0]);
            }
        },
    },
};
</script>
